
/* eslint-disable camelcase */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { AxiosError } from 'axios';

import axios from 'axios';

import {
  defineComponent,
  ref,
  computed,
} from 'vue';

import { useI18n } from 'vue-i18n';

import { useToast } from 'primevue/usetoast';

import type { ErrorsMap } from '@/types';

interface Match {
  uuid: string;
  endpoint_id: number;
  endpoint_compiled_name: string | null;
  endpoint_comment: string | null;
}

interface FoundEntry {
  value: string;
  matches: Match[];
}

interface ResolveResponse {
  found: FoundEntry[];
  not_found: string[];
}

interface EndpointGroup {
  endpoint_id: number;
  endpoint_compiled_name: string | null;
  endpoint_comment: string | null;
  text: string;
  rows: number;
}

interface ValueGroup {
  value: string;
  endpoints: EndpointGroup[];
  allText: string;
  allRows: number;
}

export default defineComponent({
  props: {
    url: {
      type: String,
      required: true,
    },
    valueLabel: {
      type: String,
      required: true,
    },
    uuidLabel: {
      type: String,
      required: true,
    },
    help: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const i18n = useI18n();

    const toast = useToast();

    const values = ref<string>('');

    const found = ref<FoundEntry[]>([]);

    const notFound = ref<string[]>([]);

    const loading = ref<boolean>(false);

    const errorsMap = ref<ErrorsMap>({});

    const groupedFound = computed<ValueGroup[]>(() => found.value.map((entry) => {
      const byEndpoint = new Map<number, { endpoint_id: number; endpoint_compiled_name: string | null; endpoint_comment: string | null; uuids: string[] }>();

      entry.matches.forEach((match) => {
        const group = byEndpoint.get(match.endpoint_id);

        if (group) {
          group.uuids.push(match.uuid);
        } else {
          byEndpoint.set(match.endpoint_id, {
            endpoint_id: match.endpoint_id,
            endpoint_compiled_name: match.endpoint_compiled_name,
            endpoint_comment: match.endpoint_comment,
            uuids: [match.uuid],
          });
        }
      });

      const allUuids = entry.matches.map((match) => match.uuid);

      return {
        value: entry.value,
        endpoints: Array.from(byEndpoint.values()).map((group) => ({
          endpoint_id: group.endpoint_id,
          endpoint_compiled_name: group.endpoint_compiled_name,
          endpoint_comment: group.endpoint_comment,
          text: group.uuids.join('\n'),
          rows: group.uuids.length,
        })),
        allText: allUuids.join('\n'),
        allRows: allUuids.length,
      };
    }));

    const allUuidsText = computed<string>(() => found.value
      .flatMap((entry) => entry.matches.map((match) => match.uuid))
      .join('\n'));

    const allUuidsRows = computed<number>(() => found.value
      .reduce((total, entry) => total + entry.matches.length, 0));

    const convert = async (): Promise<void> => {
      loading.value = true;

      errorsMap.value = {};

      try {
        const response = await axios.post<ResolveResponse>(props.url, {
          values: values.value
            .split('\n')
            .map((value) => value.trim())
            .filter(Boolean),
        });

        found.value = response.data.found;

        notFound.value = response.data.not_found;
      } catch (error: unknown | AxiosError) {
        // В случае ошибки валидации API возвращает ответ с кодом 422
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          errorsMap.value = error.response.data as ErrorsMap;

          toast.add({
            severity: 'error',
            summary: i18n.t('error'),
            detail: i18n.t('validation_error'),
            life: 2000,
          });
        } else {
          toast.add({
            severity: 'error',
            summary: i18n.t('error'),
            detail: i18n.t('unknown_error'),
            life: 2000,
          });
        }
      }

      loading.value = false;
    };

    return {
      values,
      notFound,
      loading,
      errorsMap,
      groupedFound,
      allUuidsText,
      allUuidsRows,
      convert,
    };
  },
});
